import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { BellRinging, Camera, Check, Copy, Megaphone, Plus } from '@phosphor-icons/react';
import type { BroadcastDto, BroadcastSendReportDto } from '@pickem/shared';
import type {
  BroadcastLandingScreenProps,
  CommishBroadcastScreenProps,
} from '../../screens/contracts';
import { formatKickoff } from '../PickCard';
import {
  BrandMark,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  InlineNotice,
  Input,
  SectionLabel,
  SkeletonList,
  Textarea,
} from '../ui';

// ---------------------------------------------------------------------------
// The composer
// ---------------------------------------------------------------------------

/**
 * Broadcasts — the one notification a person writes.
 *
 * Every other commissioner screen edits league state, which is recoverable: a wrong spread
 * is corrected, a wrong grade overridden, a published week unpublished. This one puts a
 * message on other people's phones, and nothing takes it back.
 *
 * So the screen is arranged around making exactly one step deliberate. Composing, saving
 * and testing are cheap and repeatable. Sending asks a second time, states the consequence
 * rather than asking "are you sure", and afterwards the whole form goes read-only instead
 * of pretending an edit would reach anybody.
 */
export function CommishBroadcast({
  isLoading,
  isError,
  error,
  retry,
  broadcasts,
  draft,
  onNewDraft,
  onEditDraft,
  onCloseDraft,
  onSaveDraft,
  isSaving,
  onChooseImage,
  onRemoveImage,
  isUploadingImage,
  pendingImageUrl,
  onTest,
  isTesting,
  onSend,
  isSending,
  sendReport,
  onDelete,
  landingUrlFor,
  actionError,
}: CommishBroadcastScreenProps) {
  if (isError && error) return <ErrorState message={error} onRetry={retry} />;
  if (isLoading) return <SkeletonList rows={4} height="h-20" />;

  if (draft) {
    return (
      <Composer
        draft={draft}
        onClose={onCloseDraft}
        onSave={onSaveDraft}
        isSaving={isSaving}
        onChooseImage={onChooseImage}
        onRemoveImage={onRemoveImage}
        isUploadingImage={isUploadingImage}
        pendingImageUrl={pendingImageUrl}
        onTest={onTest}
        isTesting={isTesting}
        onSend={onSend}
        isSending={isSending}
        sendReport={sendReport}
        landingUrlFor={landingUrlFor}
        actionError={actionError}
      />
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-field-500">
            League
          </div>
          <h1 className="mt-1.5 text-3xl font-semibold tracking-tighter">Broadcasts</h1>
        </div>
        <Button size="sm" onClick={onNewDraft} className="mt-1 shrink-0">
          <Plus size={15} weight="bold" />
          New
        </Button>
      </div>

      <InlineNotice tone="info">
        A notification you write yourself, sent to everyone in the league who has them
        turned on. It reaches phones directly and cannot be recalled, so there is a test
        send that goes only to you.
      </InlineNotice>

      {actionError && <InlineNotice tone="error">{actionError}</InlineNotice>}

      {broadcasts.length === 0 ? (
        <EmptyState
          title="Nothing sent yet"
          description="Write one for an early heads-up, a scheduling change, or something less serious."
          icon={<Megaphone size={30} />}
        />
      ) : (
        <div className="space-y-2.5">
          {broadcasts.map((item) => (
            <Card key={item.id} className="p-4">
              <div className="flex items-start gap-3">
                {item.imageUrl && (
                  <img
                    src={item.imageUrl}
                    alt=""
                    className="size-12 shrink-0 rounded-lg border border-line object-cover"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-field-100">{item.title}</div>
                  <div className="mt-0.5 line-clamp-2 text-[12px] leading-relaxed text-field-400">
                    {item.body}
                  </div>
                  <div className="mt-1.5 font-mono text-[11px] text-field-600">
                    {item.sentAt
                      ? `Sent ${formatKickoff(item.sentAt)} · ${item.deliveredCount ?? 0} device${item.deliveredCount === 1 ? '' : 's'}`
                      : 'Draft'}
                  </div>
                </div>

                <div className="flex shrink-0 flex-col items-end gap-1.5">
                  {item.sentAt && (
                    <span className="rounded bg-win/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-win">
                      Sent
                    </span>
                  )}
                  {/*
                    A sent broadcast still opens — read-only, but it is the only place its
                    landing link can be copied from, and that link outlives the
                    notification that carried it.
                  */}
                  <Button size="sm" variant="outline" onClick={() => onEditDraft(item.id)}>
                    {item.sentAt ? 'View' : 'Edit'}
                  </Button>
                  <button
                    type="button"
                    onClick={() => onDelete(item.id)}
                    className="font-mono text-[10px] uppercase tracking-[0.14em] text-field-600 transition-colors hover:text-loss"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

interface ComposerProps {
  draft: BroadcastDto;
  onClose: () => void;
  onSave: CommishBroadcastScreenProps['onSaveDraft'];
  isSaving: boolean;
  onChooseImage: (file: File) => void;
  onRemoveImage: () => void;
  isUploadingImage: boolean;
  pendingImageUrl: string | null;
  onTest: () => void;
  isTesting: boolean;
  onSend: () => void;
  isSending: boolean;
  sendReport: BroadcastSendReportDto | null;
  landingUrlFor: (path: string) => string;
  actionError: string | null;
}

function Composer({
  draft,
  onClose,
  onSave,
  isSaving,
  onChooseImage,
  onRemoveImage,
  isUploadingImage,
  pendingImageUrl,
  onTest,
  isTesting,
  onSend,
  isSending,
  sendReport,
  landingUrlFor,
  actionError,
}: ComposerProps) {
  const sent = draft.sentAt !== null;
  const imageInput = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState(false);
  const [confirmingSend, setConfirmingSend] = useState(false);

  const [values, setValues] = useState({
    title: draft.title,
    body: draft.body,
    landingKicker: draft.landingKicker ?? '',
    landingHeadline: draft.landingHeadline ?? '',
    landingBody: draft.landingBody ?? '',
    targetPath: draft.targetPath ?? '',
  });
  const set = (key: keyof typeof values) => (value: string) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const blank = (v: string) => (v.trim() ? v.trim() : null);
  const save = () =>
    onSave({
      title: values.title,
      body: values.body,
      landingKicker: blank(values.landingKicker),
      landingHeadline: blank(values.landingHeadline),
      landingBody: blank(values.landingBody),
      targetPath: blank(values.targetPath),
    });

  const image = pendingImageUrl ?? draft.imageUrl;
  const landingUrl = landingUrlFor(draft.landingPath);
  const usesLandingPage = !values.targetPath.trim();

  return (
    <div className="space-y-6">
      <div>
        <button
          type="button"
          onClick={onClose}
          className="font-mono text-[11px] uppercase tracking-[0.16em] text-field-500 transition-colors hover:text-field-300"
        >
          &larr; All broadcasts
        </button>
        <h1 className="mt-2 text-3xl font-semibold tracking-tighter">
          {sent ? 'Sent broadcast' : 'New broadcast'}
        </h1>
      </div>

      {sent && (
        <InlineNotice tone="info">
          This one has gone out, so it can no longer be edited. The notification cannot be
          recalled and the landing page&apos;s address is already on people&apos;s phones —
          changing what it says now would rewrite something nobody could see happening.
        </InlineNotice>
      )}

      {actionError && <InlineNotice tone="error">{actionError}</InlineNotice>}

      {/* The notification -------------------------------------------------- */}
      <section className="space-y-4">
        <SectionLabel>The notification</SectionLabel>

        <Field label="Title" hint="Most phones cut this off around 40 characters.">
          <Input
            value={values.title}
            disabled={sent}
            maxLength={120}
            onChange={(e) => set('title')(e.target.value)}
            placeholder="Breaking"
          />
        </Field>

        <Field label="Message">
          <Textarea
            value={values.body}
            disabled={sent}
            maxLength={400}
            rows={3}
            onChange={(e) => set('body')(e.target.value)}
            placeholder="What it says under the title."
          />
        </Field>

        <ImageField
          image={image}
          sent={sent}
          isUploading={isUploadingImage}
          inputRef={imageInput}
          onChoose={onChooseImage}
          onRemove={onRemoveImage}
        />
      </section>

      {/* Where it goes ----------------------------------------------------- */}
      <section className="space-y-4 border-t border-line pt-6">
        <SectionLabel>Where tapping it goes</SectionLabel>

        <Field
          label="Path"
          hint="Leave empty to use the page below. Or point at something that already exists — /picks, /standings."
        >
          <Input
            value={values.targetPath}
            disabled={sent}
            onChange={(e) => set('targetPath')(e.target.value)}
            placeholder="/picks"
          />
        </Field>

        {usesLandingPage && (
          <>
            <Field label="Kicker" hint="Small label above the headline. Optional.">
              <Input
                value={values.landingKicker}
                disabled={sent}
                maxLength={60}
                onChange={(e) => set('landingKicker')(e.target.value)}
                placeholder="Breaking news"
              />
            </Field>

            <Field label="Headline" hint="Falls back to the notification title if left empty.">
              <Input
                value={values.landingHeadline}
                disabled={sent}
                maxLength={200}
                onChange={(e) => set('landingHeadline')(e.target.value)}
              />
            </Field>

            <Field label="Page text" hint="Falls back to the notification message if left empty.">
              <Textarea
                value={values.landingBody}
                disabled={sent}
                maxLength={4000}
                rows={5}
                onChange={(e) => set('landingBody')(e.target.value)}
              />
            </Field>

            {sent && (
              <div className="flex items-center gap-2 rounded-xl border border-line bg-field-900 px-3 py-2.5">
                <span className="min-w-0 flex-1 truncate font-mono text-[12px] text-field-300">
                  {landingUrl}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    void navigator.clipboard.writeText(landingUrl).then(() => {
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1600);
                    });
                  }}
                  className="flex shrink-0 items-center gap-1 font-mono text-[10px] uppercase tracking-[0.14em] text-signal"
                >
                  {copied ? <Check size={12} weight="bold" /> : <Copy size={12} weight="bold" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* Actions ------------------------------------------------------------ */}
      {!sent && (
        <section className="space-y-3 border-t border-line pt-6">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={save} loading={isSaving}>
              Save draft
            </Button>
            <Button variant="ghost" onClick={onTest} loading={isTesting}>
              <BellRinging size={15} weight="fill" />
              Send to me only
            </Button>
          </div>

          <p className="text-[12px] leading-relaxed text-field-500">
            Save first, then test — the test sends what is stored, not what is on screen.
            It goes to your devices and nobody else&apos;s.
          </p>

          {/*
            The only irreversible control here, so the only one that asks twice. The
            confirmation states the consequence rather than asking "are you sure", which is
            a question nobody has ever answered thoughtfully.
          */}
          <div className="rounded-card border border-loss/30 bg-loss/[0.06] p-4">
            {confirmingSend ? (
              <div className="space-y-3">
                <div className="text-sm leading-relaxed text-field-100">
                  This sends{' '}
                  <span className="font-medium">{draft.title || 'this broadcast'}</span> to
                  every person in the league with notifications on. It cannot be recalled,
                  and it cannot be edited afterwards.
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setConfirmingSend(false);
                      onSend();
                    }}
                    loading={isSending}
                  >
                    Send it
                  </Button>
                  <Button variant="ghost" onClick={() => setConfirmingSend(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-field-100">Send to the league</div>
                  <div className="mt-0.5 text-[12px] text-field-500">
                    Everyone with notifications on.
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="shrink-0"
                  onClick={() => setConfirmingSend(true)}
                >
                  <Megaphone size={15} weight="fill" />
                  Send
                </Button>
              </div>
            )}
          </div>
        </section>
      )}

      {sendReport && (
        <InlineNotice tone={sendReport.delivered > 0 ? 'success' : 'warn'}>
          {sendReport.note}
        </InlineNotice>
      )}
    </div>
  );
}

/**
 * The image.
 *
 * Shown at its real proportions rather than cropped to a thumbnail, because what it looks
 * like is the entire reason for attaching one. Android crops it again on its own terms in
 * the notification shade, which nothing here can change — so the preview shows the whole
 * picture and the copy says where the crop happens.
 */
function ImageField({
  image,
  sent,
  isUploading,
  inputRef,
  onChoose,
  onRemove,
}: {
  image: string | null;
  sent: boolean;
  isUploading: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onChoose: (file: File) => void;
  onRemove: () => void;
}) {
  return (
    <div className="space-y-2">
      <div className="font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-field-400">
        Image
      </div>

      {image ? (
        <div className="overflow-hidden rounded-xl border border-line bg-field-900">
          <img src={image} alt="" className="max-h-56 w-full object-contain" />
          {!sent && (
            <div className="flex gap-2 border-t border-line p-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => inputRef.current?.click()}
                loading={isUploading}
              >
                Replace
              </Button>
              <Button size="sm" variant="ghost" onClick={onRemove} disabled={isUploading}>
                Remove
              </Button>
            </div>
          )}
        </div>
      ) : sent ? (
        <div className="rounded-xl border border-dashed border-line px-3 py-4 text-[12px] text-field-600">
          No image.
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex w-full items-center gap-3 rounded-xl border border-dashed border-line px-3.5 py-4 text-left transition-colors hover:border-line-strong"
        >
          <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-field-800 text-field-400">
            <Camera size={17} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[13px] text-field-200">
              {isUploading ? 'Uploading…' : 'Add an image'}
            </span>
            <span className="mt-0.5 block text-[12px] leading-relaxed text-field-500">
              Drawn inside the notification on Android, and at the top of the landing page
              everywhere. Shrunk on your device before it is sent.
            </span>
          </span>
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onChoose(file);
          // Cleared so picking the same file twice still fires a change event.
          e.target.value = '';
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// The landing page
// ---------------------------------------------------------------------------

/**
 * Where a broadcast's notification lands.
 *
 * Rendered outside the app shell and outside the auth guard. Someone tapping a
 * notification arrives from their lock screen, and a redirect to a login form would be the
 * one outcome that guarantees the message never gets read.
 *
 * Composed like a story rather than like a settings page — kicker, headline, image, body —
 * because the commissioner is writing something to be read, not a form to be filled in.
 */
export function BroadcastLanding({
  isLoading,
  isError,
  landing,
  homeHref,
}: BroadcastLandingScreenProps) {
  if (isLoading) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-16">
        <SkeletonList rows={3} height="h-8" />
      </div>
    );
  }

  if (isError || !landing) {
    return (
      <div className="grid min-h-[100dvh] place-items-center px-5">
        <div className="text-center">
          <div className="font-mono text-6xl font-semibold tracking-tighter text-field-800">
            404
          </div>
          <p className="mt-4 text-lg font-medium text-field-100">That page isn&apos;t here</p>
          <p className="mt-1.5 text-sm text-field-400">
            The link may be out of date, or the notification it came from was withdrawn.
          </p>
          <Link to={homeHref} className="mt-6 inline-block">
            <Button variant="outline">Go to Pick&apos;em</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="taste-root min-h-[100dvh] bg-field-900">
      <header className="taste-safe-top border-b border-line bg-nav">
        <div className="mx-auto flex h-14 w-full max-w-2xl items-center gap-2.5 px-5">
          <BrandMark className="size-6" />
          <span className="text-[14px] font-semibold tracking-tight text-field-100">
            Otoe Pick&apos;em
          </span>
        </div>
      </header>

      <article className="mx-auto max-w-2xl px-5 py-10">
        {landing.kicker && (
          <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-live">
            {landing.kicker}
          </div>
        )}

        <h1 className="mt-3 text-3xl font-semibold leading-[1.08] tracking-tighter text-field-100 sm:text-5xl">
          {landing.headline}
        </h1>

        {landing.sentAt && (
          <div className="mt-4 font-mono text-[11px] uppercase tracking-[0.14em] text-field-600">
            {formatKickoff(landing.sentAt)}
          </div>
        )}

        {landing.imageUrl && (
          <img
            src={landing.imageUrl}
            alt=""
            className="mt-7 w-full rounded-card border border-line"
          />
        )}

        {landing.body && (
          // `whitespace-pre-line` so the paragraph breaks the commissioner typed survive.
          // Rendered as text, never as markup — this is user-authored copy.
          <div className="mt-7 whitespace-pre-line text-[16px] leading-relaxed text-field-300">
            {landing.body}
          </div>
        )}

        <div className="mt-10 border-t border-line pt-6">
          <Link to={homeHref}>
            <Button variant="outline">Back to Pick&apos;em</Button>
          </Link>
        </div>
      </article>
    </div>
  );
}
